<script setup>
import { computed, onMounted, onBeforeUnmount, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import toast from 'dashboard/helper/toast';
import { intlLocale } from 'dashboard/helper/intlLocale';
import { useI18n } from 'vue-i18n';
import { convertSecondsToTimeUnit } from '@chatwoot/utils';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

import AddSLA from './AddSLA.vue';
import PageLayout from 'dashboard/components-next/layout/PageLayout.vue';
import SLAEmptyState from './components/SLAEmptyState.vue';
import SLAHeader from './components/SLAHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import SLAPaywallEnterprise from './components/SLAPaywallEnterprise.vue';
import DeleteSlaDialog from 'dashboard/components-next/sla/DeleteSlaDialog.vue';

const store = useStore();
const router = useRouter();
const { t, locale } = useI18n();

const loading = ref({});
const showAddPopup = ref(false);
const deleteDialogRef = ref(null);
const selectedResponse = ref(null);

const isOnChatwootCloud = useMapGetter('globalConfig/isOnChatwootCloud');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);
const records = useMapGetter('sla/getSLA');
const currentUser = useMapGetter('getCurrentUser');
const accountId = useMapGetter('getCurrentAccountId');
const uiFlags = useMapGetter('sla/getUIFlags');

const isBehindAPaywall = computed(
  () => !isFeatureEnabledonAccount.value(accountId.value, 'sla')
);
const isSuperAdmin = computed(() => currentUser.value?.type === 'SuperAdmin');

const openAddPopup = () => {
  if (isBehindAPaywall.value) return;
  showAddPopup.value = true;
};

const hideAddPopup = () => {
  showAddPopup.value = false;
};

const openDeletePopup = response => {
  selectedResponse.value = response;
  deleteDialogRef.value?.open();
};

const deleteSla = async sla => {
  loading.value[sla.id] = true;
  try {
    await store.dispatch('sla/delete', sla.id);
    toast.success(t('SLA.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    toast.error(t('SLA.DELETE.API.ERROR_MESSAGE'));
  } finally {
    loading.value[sla.id] = false;
  }
};

const displayTime = threshold => {
  const { time, unit } = convertSecondsToTimeUnit(threshold, {
    minute: 'm',
    hour: 'h',
    day: 'd',
  });
  if (!time) return '-';
  return `${time}${unit}`;
};

const formatCount = count =>
  new Intl.NumberFormat(intlLocale(locale.value)).format(count ?? 0);

const onClickCTA = () => {
  router.push({
    name: 'billing_settings_index',
    params: { accountId: accountId.value },
  });
};

// Sidebar dispara este evento via createSidebarActionHandler.new-sla.
// Reforco do paywall aqui pra evitar abrir o modal mesmo que a sidebar
// nao tenha conhecimento dele.
const handleSidebarSlaNew = () => {
  openAddPopup();
};

onMounted(() => {
  emitter.on(BUS_EVENTS.SIDEBAR_OPEN_SLA_NEW, handleSidebarSlaNew);
  store.dispatch('sla/get');
});

onBeforeUnmount(() => {
  emitter.off(BUS_EVENTS.SIDEBAR_OPEN_SLA_NEW, handleSidebarSlaNew);
});
</script>

<template>
  <PageLayout>
    <template #header>
      <SLAHeader />
    </template>
    <woot-loading-state
      v-if="uiFlags.isFetching"
      :message="$t('SLA.LOADING')"
    />
    <template v-else>
      <SLAPaywallEnterprise
        v-if="isBehindAPaywall"
        :is-super-admin="isSuperAdmin"
        :is-on-chatwoot-cloud="isOnChatwootCloud"
        @upgrade="onClickCTA"
      />
      <SLAEmptyState
        v-else-if="!records.length"
        @primary-action="openAddPopup"
      />
      <table v-else class="w-full divide-y divide-n-weak">
        <thead class="text-left">
          <tr class="text-sm text-n-slate-11">
            <th class="py-3 font-semibold">
              {{ $t('SLA.LIST.TABLE_HEADER.NAME') }}
            </th>
            <th
              class="py-3 font-semibold text-right whitespace-nowrap ltr:pl-6 rtl:pr-6"
            >
              <span
                v-tooltip.top="
                  $t('SLA.LIST.TABLE_TOOLTIP.FIRST_RESPONSE') +
                  ' (' +
                  $t('SLA.LIST.TABLE_TOOLTIP.EMPTY') +
                  ')'
                "
                class="underline decoration-dotted underline-offset-4 cursor-help"
              >
                {{ $t('SLA.LIST.TABLE_HEADER.FIRST_RESPONSE') }}
              </span>
            </th>
            <th
              class="py-3 font-semibold text-right whitespace-nowrap ltr:pl-6 rtl:pr-6"
            >
              <span
                v-tooltip.top="
                  $t('SLA.LIST.TABLE_TOOLTIP.NEXT_RESPONSE') +
                  ' (' +
                  $t('SLA.LIST.TABLE_TOOLTIP.EMPTY') +
                  ')'
                "
                class="underline decoration-dotted underline-offset-4 cursor-help"
              >
                {{ $t('SLA.LIST.TABLE_HEADER.NEXT_RESPONSE') }}
              </span>
            </th>
            <th
              class="py-3 font-semibold text-right whitespace-nowrap ltr:pl-6 rtl:pr-6"
            >
              <span
                v-tooltip.top="
                  $t('SLA.LIST.TABLE_TOOLTIP.RESOLUTION') +
                  ' (' +
                  $t('SLA.LIST.TABLE_TOOLTIP.EMPTY') +
                  ')'
                "
                class="underline decoration-dotted underline-offset-4 cursor-help"
              >
                {{ $t('SLA.LIST.TABLE_HEADER.RESOLUTION') }}
              </span>
            </th>
            <th
              class="py-3 font-semibold text-right whitespace-nowrap ltr:pl-6 rtl:pr-6"
            >
              <span
                v-tooltip.top="$t('SLA.LIST.TABLE_TOOLTIP.CONVERSATIONS')"
                class="underline decoration-dotted underline-offset-4 cursor-help"
              >
                {{ $t('SLA.LIST.TABLE_HEADER.CONVERSATIONS') }}
              </span>
            </th>
            <th
              class="py-3 font-semibold text-right whitespace-nowrap ltr:pl-6 rtl:pr-6"
            >
              {{ $t('SLA.LIST.TABLE_HEADER.ACTIONS') }}
            </th>
          </tr>
        </thead>
        <tbody class="divide-y divide-n-weak text-n-slate-11">
          <tr v-for="sla in records" :key="sla.id">
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              <span class="block font-medium text-n-slate-12">
                {{ sla.name }}
              </span>
              <span class="block text-sm">
                <template v-if="sla.description">
                  {{ sla.description }} ·
                </template>
                {{
                  sla.only_during_business_hours
                    ? $t('SLA.LIST.BUSINESS_HOURS_ONLY')
                    : $t('SLA.LIST.ALL_HOURS')
                }}
              </span>
            </td>
            <td
              class="py-4 text-right tabular-nums whitespace-nowrap ltr:pl-6 rtl:pr-6"
            >
              {{ displayTime(sla.first_response_time_threshold) }}
            </td>
            <td
              class="py-4 text-right tabular-nums whitespace-nowrap ltr:pl-6 rtl:pr-6"
            >
              {{ displayTime(sla.next_response_time_threshold) }}
            </td>
            <td
              class="py-4 text-right tabular-nums whitespace-nowrap ltr:pl-6 rtl:pr-6"
            >
              {{ displayTime(sla.resolution_time_threshold) }}
            </td>
            <td
              class="py-4 text-right tabular-nums whitespace-nowrap ltr:pl-6 rtl:pr-6"
            >
              {{ formatCount(sla.conversations_count) }}
            </td>
            <td class="py-4 ltr:pl-6 rtl:pr-6">
              <div class="flex justify-end gap-1">
                <Button
                  v-tooltip.top="$t('SLA.FORM.DELETE')"
                  icon="i-lucide-trash-2"
                  xs
                  ruby
                  faded
                  :is-loading="loading[sla.id]"
                  @click="openDeletePopup(sla)"
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </template>

    <woot-modal v-model:show="showAddPopup" :on-close="hideAddPopup">
      <AddSLA @close="hideAddPopup" />
    </woot-modal>

    <DeleteSlaDialog
      ref="deleteDialogRef"
      :sla="selectedResponse"
      @confirm="deleteSla"
    />
  </PageLayout>
</template>
